import {FormattedMessage} from 'react-intl';
import React from 'react';

let logoutFunction;
let failAlertFunction;
let flashAlertTimeoutMs;
const HTTP_UNAUTHORIZED = 401;
const HTTP_FORBIDDEN = 403;
let sessionReadyPromise;
// only an established (guest-email or SSO) session may be torn down by a 401
let hasSessionProvider = () => false;
// guards against every in-flight request scheduling its own logout
let logoutScheduled = false;

const getFormattedMessage = () => {
    return (<FormattedMessage
        defaultMessage='Your session has expired or tampered ! Please try again'
        id='fetch-json.util.sessionExpired'
    />);
};

/**
 * Registers a predicate telling the util whether a user session actually exists.
 * Without it a 401 from an anonymous/bootstrap call would force a pointless logout.
 * @param {Function} provider Returns truthy when a user session is established.
 * @returns {undefined}
 */
export function setSessionProvider(provider) {
    hasSessionProvider = provider;
}

function forceLogout() {
    // An anonymous visitor has nothing to log out of - a 401 here is a backend
    // credential problem, not an expired session. Logging out would just loop.
    if (!hasSessionProvider()) {
        return;
    }

    if (logoutScheduled) {
        return;
    }
    logoutScheduled = true;

    if (failAlertFunction) {
        failAlertFunction(getFormattedMessage());
    }
    if (logoutFunction) {
        setTimeout(logoutFunction, flashAlertTimeoutMs);
    }
}

function handleUnauthorized(response) {
    if (response.status === HTTP_UNAUTHORIZED) {
        forceLogout();
        return Promise.reject(response);
    }

    return response.clone().text().then((text) => {
        if (text && text.includes('<code>401</code>')) {
            forceLogout();
            return Promise.reject(response);
        }

        return response;
    }).catch(() => {
        return response;
    });
}

function makeSessionConfig(init = {}) {
    return {
        ...init,
        credentials: 'include',
        headers: init.headers || {}
    };
}

/**
 * Only used by pc-user-session.class
 * @param {Function} logout The function to force a logout.
 * @returns {undefined}
 */
export function setLogoutFunction(logout) {
    logoutFunction = logout;
}

export function setFailAlertFunction(failAlert, timeout) {
    failAlertFunction = failAlert;
    flashAlertTimeoutMs = timeout;
}

/**
 * Calls fetch and converts the response to JSON.
 * @param {*} input See fetch API for details.
 * @param  {*} init See fetch API for details.
 * @returns {Promise} resolved with the JSON for a 200, rejected otherwise
 */
export function fetchJson(input, init) {
    return fetch(input, init)
        .then(handleUnauthorized)
        .then((response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error(`Request failed with status ${response.status}`);
        }
        );
}

function createAnonymousSession() {
    return fetch(
        AUTH_SESSION_GUEST_URL,
        makeSessionConfig({
            method: 'POST'
        })
    ).then((response) => {
        if (response.ok) {
            return response;
        }
        return Promise.reject(response);
    });
}

export function ensureSession() {
    if (sessionReadyPromise) {
        return sessionReadyPromise;
    }

    sessionReadyPromise = fetch(AUTH_SESSION_ME_URL, makeSessionConfig())
        .then((response) => {
            if (response.ok) {
                return response;
            }

            if (response.status === HTTP_FORBIDDEN) {
                return createAnonymousSession();
            }

            return Promise.reject(response);
        })
        .then(() => undefined)
        .catch((error) => {
            sessionReadyPromise = null;
            return Promise.reject(error);
        });

    return sessionReadyPromise;
}

export function resetSessionBootstrap() {
    sessionReadyPromise = null;
    logoutScheduled = false;
}

/**
 * Calls fetch with the bearer token authorization.
 * Logs the user out if the server sends a 401.
 * @param {*} input See fetch API for details.
 * @param  {*} init See fetch API for details.
 * @returns {Promise} See fetch API for details.  If a 401 is sent the promise will be rejected.
 */
export function fetchWithAuth(input, init) {
    return ensureSession().then(() => {
        return fetch(input, makeSessionConfig(init))
            .then(handleUnauthorized)
            .then((response) => {
                if (response.ok) {
                    return response;
                }
                return Promise.reject(response);
            });
    });
}

/**
 * Calls fetchWithAuth and converts the response to JSON.
 * @param {*} input See fetch API for details.
 * @param  {*} init See fetch API for details.
 * @returns {Promise} resolved with the JSON for a 200, rejected otherwise
 */
export function fetchJsonWithAuth(input, init) {
    return fetchWithAuth(input, init).then(
        (response) => {
            if (response.ok) {
                return response.json();
            }
            throw new Error(`Request failed with status ${response.status}`);
        }
    );
}

/**
 * Make the configuration required for requests that send the app ID authorization.
 * @param {Object} [init] Add the configuration to this object.
 * @returns {Object} The fetch configuration object with authorization headers populated.
 */
export function makeAppIdConfig(init = {}) {
    return makeSessionConfig(init);
}

export function generateTempAuthToken() {
    return ensureSession().then(() => {
        return undefined;
    }).catch(() => {
        return undefined;
    });
}

/**
 * Validate temp authorization token expiry then make network call.
 * @param {*} input See fetch API for details.
 * @param {Object} [init] Add the configuration to this object.
 * @returns {Promise} resolved with the JSON for a 200, rejected otherwise
 */
export function fetchJsonWithTempToken(input, init) {
    return ensureSession().then(() => {
        return fetchJson(input, makeAppIdConfig(init));
    });
}

/**
 * Calls fetch and return the text response.
 * @param {*} input See fetch API for details.
 * @param  {*} init See fetch API for details.
 * @returns {Promise} resolved with the text for a 200, rejected otherwise
 */
export function fetchText(input, init) {
    return fetch(input, init)
        .then(handleUnauthorized)
        .then((response) => {
            if (response.ok) {
                return response.text();
            }
            throw new Error(`Request failed with status ${response.status}`);
        }
        );
}

/**
 * Validate temp authorization token expiry then make network call.
 * @param {*} input See fetch API for details.
 * @param {Object} [init] Add the configuration to this object.
 * @returns {Promise} resolved with the text for a 200, rejected otherwise
 */
export function fetchTextWithTempToken(input, init) {
    return ensureSession().then(() => {
        return fetchText(input, makeAppIdConfig(init));
    });
}
